import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Spinner from "../components/ui/Spinner";
import logo from "../assets/logo.png";
import { authApi } from "../api/authApi";

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token")?.trim() ?? "", [searchParams]);
  const emailParam = useMemo(() => searchParams.get("email")?.trim() ?? "", [searchParams]);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const missingLink = !token || !emailParam;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await authApi.resetPassword({
        email: emailParam,
        token,
        newPassword,
      });
      setSuccess(true);
    } catch (e) {
      const err = e as Error & { apiErrors?: string[] };
      if (err.apiErrors?.includes("password_policy")) {
        setError(err.message || "Password must be at least 8 characters.");
      } else {
        setError("Invalid or expired reset link. Please request a new one.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 px-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900/80 p-8 shadow-2xl shadow-blue-900/20">
        <div className="mb-8 text-center">
          <img
            src={logo}
            alt="IntelliWarrior"
            className="mx-auto mb-4 h-40 w-auto max-w-full object-contain"
          />
          <h1 className="text-lg font-semibold text-slate-100">Set a new password</h1>
          <p className="mt-1 text-sm text-slate-400">Choose a strong password for your account.</p>
        </div>

        {missingLink ? (
          <div className="space-y-6">
            <p className="rounded-lg border border-amber-500/30 bg-amber-900/20 px-3 py-3 text-sm text-amber-200">
              Invalid or expired reset link. Please request a new one.
            </p>
            <p className="text-center text-sm text-slate-400">
              <Link to="/forgot-password" className="font-medium text-emerald-400 hover:text-emerald-300">
                Request a new reset link
              </Link>
            </p>
            <p className="text-center text-sm text-slate-400">
              <Link to="/login" className="text-slate-500 hover:text-slate-300">
                Back to login
              </Link>
            </p>
          </div>
        ) : success ? (
          <div className="space-y-6">
            <p className="rounded-lg border border-emerald-500/30 bg-emerald-900/20 px-3 py-3 text-center text-sm text-emerald-200">
              Password reset successfully!
            </p>
            <p className="text-center text-sm text-slate-400">
              <Link to="/login" className="font-medium text-emerald-400 hover:text-emerald-300">
                Sign in
              </Link>
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="mb-1 block text-sm text-slate-300">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={emailParam}
                readOnly
                className="w-full cursor-not-allowed rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2 text-slate-400"
              />
            </div>

            <div>
              <label htmlFor="newPassword" className="mb-1 block text-sm text-slate-300">
                New password
              </label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100 outline-none ring-blue-500 placeholder:text-slate-500 focus:ring-2"
                placeholder="At least 8 characters"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="mb-1 block text-sm text-slate-300">
                Confirm password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100 outline-none ring-blue-500 placeholder:text-slate-500 focus:ring-2"
                placeholder="Repeat password"
              />
            </div>

            {error && (
              <div className="space-y-3 rounded-lg border border-red-500/40 bg-red-900/20 px-3 py-2 text-sm text-red-300">
                <p>{error}</p>
                <Link to="/forgot-password" className="block font-medium text-emerald-400 hover:text-emerald-300">
                  Request a new reset link
                </Link>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Spinner className="h-4 w-4 border-2 border-white/30 border-t-white" />
                  Updating…
                </>
              ) : (
                "Update password"
              )}
            </button>

            <p className="text-center text-sm text-slate-400">
              <Link to="/login" className="text-slate-500 hover:text-slate-300">
                Back to login
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
